import { useState, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import type { SARadio, SARadioPlan } from '@/mocks/superAdminRadios';

interface ChangePlanModalProps {
  radio: SARadio | null;
  onClose: () => void;
  onConfirm: (radio: SARadio, newPlan: SARadioPlan) => void;
}

type PlanId = SARadioPlan;

interface PlanCard {
  id: PlanId;
  name: string;
  price: string;
  priceNum: number;
  storageGB: number;
  icon: string;
  color: string;
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  badge: string;
  badgeBg: string;
  badgeText: string;
  features: string[];
}

const plans: PlanCard[] = [
  {
    id: 'free',
    name: 'Free',
    price: 'Gratis',
    priceNum: 0,
    storageGB: 1,
    icon: 'ri-radio-line',
    color: 'text-secondary-600',
    bg: 'bg-secondary-50/60',
    border: 'border-secondary-300',
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-600',
    badge: 'Starter',
    badgeBg: 'bg-secondary-100',
    badgeText: 'text-secondary-600',
    features: ['Perfil básico', 'Stream URL', 'Cabina limitada', '1 GB storage'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$29/mes',
    priceNum: 29,
    storageGB: 50,
    icon: 'ri-star-fill',
    color: 'text-primary-600',
    bg: 'bg-primary-50/60',
    border: 'border-primary-300',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-600',
    badge: 'Más popular',
    badgeBg: 'bg-primary-100',
    badgeText: 'text-primary-600',
    features: ['Fotos y galerías', 'Posts con imágenes', 'Comunidad completa', '50 GB storage', '5 locutores'],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$59/mes',
    priceNum: 59,
    storageGB: 500,
    icon: 'ri-vip-diamond-fill',
    color: 'text-accent-600',
    bg: 'bg-accent-50/60',
    border: 'border-accent-300',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
    badge: 'Profesional',
    badgeBg: 'bg-accent-100',
    badgeText: 'text-accent-600',
    features: ['Videos y Lives', 'Analytics avanzado', 'Soporte prioritario', '500 GB storage', 'Locutores ilimitados', 'API de acceso'],
  },
];

const planOrder: PlanId[] = ['free', 'pro', 'premium'];

function swal() {
  return Swal.mixin({
    customClass: {
      popup: 'rounded-2xl',
      title: 'font-bold text-sm',
      confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      htmlContainer: 'text-sm',
    },
    buttonsStyling: false,
  });
}

export default function ChangePlanModal({ radio, onClose, onConfirm }: ChangePlanModalProps) {
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId | null>(null);
  const [includeAnnual, setIncludeAnnual] = useState(false);
  const [step, setStep] = useState<'select' | 'confirm'>('select');

  const currentPlanDef = plans.find((p) => p.id === radio?.plan);
  const selectedPlanDef = plans.find((p) => p.id === selectedPlanId);

  const currentIndex = radio ? planOrder.indexOf(radio.plan) : -1;
  const selectedIndex = selectedPlanId ? planOrder.indexOf(selectedPlanId) : -1;
  const isUpgrade = selectedIndex > currentIndex;
  const isDowngrade = selectedIndex >= 0 && selectedIndex < currentIndex;

  const downgradeBlocked = useMemo(() => {
    if (!radio || !selectedPlanDef || !isDowngrade) return false;
    return radio.storageUsedGB > selectedPlanDef.storageGB;
  }, [radio, selectedPlanDef, isDowngrade]);

  const monthlyPrice = selectedPlanDef?.priceNum ?? 0;
  const annualPrice = Math.round(monthlyPrice * 10);
  const totalToday = includeAnnual ? annualPrice : monthlyPrice;
  const nextRenewal = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  const storageDiff = useMemo(() => {
    if (!radio || !selectedPlanDef) return '';
    const diff = selectedPlanDef.storageGB - radio.storageTotalGB;
    if (diff > 0) return `+${diff} GB`;
    if (diff < 0) return `${diff} GB`;
    return 'Sin cambios';
  }, [radio, selectedPlanDef]);

  const gainedFeatures = useMemo(() => {
    if (!selectedPlanDef || !currentPlanDef || !isUpgrade) return [];
    return selectedPlanDef.features.filter((f) => !currentPlanDef.features.includes(f));
  }, [selectedPlanDef, currentPlanDef, isUpgrade]);

  const lostFeatures = useMemo(() => {
    if (!selectedPlanDef || !currentPlanDef || !isDowngrade) return [];
    return currentPlanDef.features.filter((f) => !selectedPlanDef.features.includes(f));
  }, [selectedPlanDef, currentPlanDef, isDowngrade]);

  const handleSelectPlan = useCallback((plan: PlanCard) => {
    if (!radio) return;
    if (plan.id === radio.plan) {
      swal().fire({
        title: 'Este es el plan actual',
        text: `${radio.name} ya está en el plan ${plan.name}.`,
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#6b7280',
      });
      return;
    }
    setSelectedPlanId(plan.id);
    setIncludeAnnual(false);
  }, [radio]);

  const handleContinue = useCallback(() => {
    if (!radio || !selectedPlanDef) return;

    if (isDowngrade && downgradeBlocked) {
      swal().fire({
        title: 'Downgrade bloqueado',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:8px"><strong>${radio.name}</strong> está usando <strong>${radio.storageUsedGB.toFixed(1)} GB</strong> de almacenamiento.</p>
          <p style="margin-bottom:8px">El plan <strong>${selectedPlanDef.name}</strong> solo incluye <strong>${selectedPlanDef.storageGB} GB</strong>.</p>
          <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:10px">
            <p style="color:#dc2626;font-weight:600;margin:0;font-size:12px"><i class="ri-alert-line"></i> No se puede bajar de plan</p>
            <p style="color:#dc2626;margin:4px 0 0;font-size:11px">El almacenamiento actual (${radio.storageUsedGB.toFixed(1)} GB) excede el límite del nuevo plan (${selectedPlanDef.storageGB} GB).</p>
            <p style="color:#9ca3af;margin:4px 0 0;font-size:11px">Liberá espacio eliminando archivos o elegí un plan con suficiente storage.</p>
          </div>
        </div>`,
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    if (isDowngrade) {
      const s = swal();
      s.fire({
        title: '¿Bajar de plan?',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:8px"><strong>${radio.name}</strong> pasará de <strong>${currentPlanDef?.name}</strong> a <strong>${selectedPlanDef.name}</strong>.</p>
          <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px;margin-bottom:8px">
            <p style="font-weight:600;color:#92400e;margin:0 0 4px;font-size:11px"><i class="ri-alert-line"></i> Importante</p>
            <p style="color:#92400e;margin:0;font-size:11px">Al bajar de plan, la radio podría perder acceso a funciones y almacenamiento. El downgrade se aplica en la próxima renovación.</p>
          </div>
          ${lostFeatures.length > 0 ? `
            <p style="font-weight:600;color:#dc2626;font-size:11px;margin-bottom:4px">Funciones que se perderán:</p>
            <ul style="list-style:none;padding:0;margin:0">
              ${lostFeatures.map((f) => `<li style="padding:2px 0;font-size:11px;color:#dc2626"><i class="ri-close-line"></i> ${f}</li>`).join('')}
            </ul>
          ` : ''}
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, bajar de plan',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) setStep('confirm');
      });
      return;
    }

    setStep('confirm');
  }, [radio, selectedPlanDef, isDowngrade, downgradeBlocked, currentPlanDef, lostFeatures]);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!radio || !selectedPlanDef || !selectedPlanId) return;
    const s = swal();

    await s.fire({
      title: 'Generando invoice...',
      html: '<p style="font-size:13px;color:#6b7280">Preparando el cargo para el nuevo plan.</p>',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading(); },
    });
    await new Promise((r) => setTimeout(r, 1500));
    Swal.close();

    const invoiceId = `INV-${Date.now().toString(36).toUpperCase()}`;
    const label = includeAnnual ? `$${annualPrice.toFixed(2)} (licencia anual)` : `$${monthlyPrice.toFixed(2)} (mensual)`;

    const result = await s.fire({
      title: 'Invoice generada',
      html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
        <p style="margin-bottom:10px">Completá el pago para activar el nuevo plan de <strong>${radio.name}</strong>.</p>
        <div style="background:#f3f4f6;border-radius:8px;padding:12px;margin-bottom:10px">
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Radio:</span><span style="font-weight:600">${radio.name}</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Plan actual:</span><span style="font-weight:600">${currentPlanDef?.name}</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Nuevo plan:</span><span style="font-weight:600;color:#f59e0b">${selectedPlanDef.name}</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Invoice:</span><span style="font-weight:600;font-family:monospace">${invoiceId}</span>
          </div>
          <div style="border-top:1px solid #e5e7eb;margin-top:8px;padding-top:8px;display:flex;justify-content:space-between">
            <span style="font-weight:600">Total:</span><span style="font-weight:700;font-size:15px">${label}</span>
          </div>
        </div>
        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px;font-size:11px;color:#92400e">
          <i class="ri-information-line"></i> <strong>Upgrade pendiente de pago.</strong> El nuevo plan se activará cuando confirmes el pago.
        </div>
      </div>`,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Pagar ahora (mock)',
      cancelButtonText: 'Ir a Billing',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (result.isConfirmed) {
      await s.fire({
        title: 'Procesando pago...',
        html: '<p style="font-size:13px;color:#6b7280">Verificando transacción...</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => { Swal.showLoading(); },
      });
      await new Promise((r) => setTimeout(r, 2000));
      Swal.close();

      onConfirm(radio, selectedPlanId);

      await s.fire({
        title: '¡Plan activado!',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:10px"><strong>${radio.name}</strong> ahora está en <strong style="color:#f59e0b">${selectedPlanDef.name}</strong>.</p>
          <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px">
            <p style="font-weight:600;color:#166534;margin:0 0 4px;font-size:11px"><i class="ri-check-line"></i> Cambios aplicados</p>
            <ul style="list-style:none;padding:0;margin:0;font-size:11px;color:#166534">
              <li style="padding:1px 0"><i class="ri-check-line"></i> Storage: ${storageDiff}</li>
              <li style="padding:1px 0"><i class="ri-check-line"></i> Próx. renovación: ${nextRenewal}</li>
              <li style="padding:1px 0"><i class="ri-check-line"></i> Funciones desbloqueadas</li>
            </ul>
          </div>
        </div>`,
        icon: 'success',
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#10b981',
      });
    }

    onClose();
  }, [radio, selectedPlanDef, selectedPlanId, includeAnnual, monthlyPrice, annualPrice, storageDiff, nextRenewal, currentPlanDef, onConfirm, onClose]);

  const handleBack = useCallback(() => setStep('select'), []);

  if (!radio) return null;

  const storagePct = radio.storageTotalGB > 0 ? (radio.storageUsedGB / radio.storageTotalGB) * 100 : 0;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200 shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">
                {step === 'select' ? 'Cambiar plan' : 'Confirmar cambio'}
              </h3>
              <p className="text-foreground-400 text-[10px] mt-0.5">
                {radio.name} · {radio.frequency} · Actual: <span className="font-semibold">{currentPlanDef?.name}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-3 flex-1">
          {step === 'select' && (
            <>
              {/* Storage usage */}
              <div className="bg-background-100 rounded-xl p-3 border border-background-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Almacenamiento actual</span>
                  <span className="text-foreground-400 text-[9px]">{storagePct.toFixed(1)}% usado</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-foreground-800 text-xs font-mono font-bold">{radio.storageUsedGB.toFixed(1)} GB</span>
                  <span className="text-foreground-300 text-[10px]">de {radio.storageTotalGB} GB</span>
                </div>
                <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${storagePct >= 90 ? 'bg-red-500' : storagePct >= 70 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(storagePct, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Plan cards */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-2">Planes disponibles</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {plans.map((plan) => {
                    const isCurrent = plan.id === radio.plan;
                    const isSelected = plan.id === selectedPlanId;
                    return (
                      <button
                        key={plan.id}
                        onClick={() => handleSelectPlan(plan)}
                        className={`relative rounded-xl p-4 border-2 text-left transition-all cursor-pointer ${
                          isSelected
                            ? `${plan.border} ${plan.bg} ring-1 ring-offset-1 ${plan.id === 'pro' ? 'ring-primary-300' : plan.id === 'premium' ? 'ring-accent-300' : 'ring-secondary-300'}`
                            : isCurrent
                              ? `${plan.border} ${plan.bg}`
                              : 'border-background-200 bg-background-50 hover:border-background-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${plan.iconBg} flex items-center justify-center mb-2`}>
                          <i className={`${plan.icon} ${plan.iconColor} text-sm`}></i>
                        </div>
                        <p className="text-foreground-950 font-bold text-xs mb-0.5">{plan.name}</p>
                        <p className="text-foreground-400 text-[10px] mb-2">{plan.price}</p>
                        <div className="flex items-center gap-1.5 mb-2">
                          <i className="ri-hard-drive-2-line text-foreground-400 text-[9px]"></i>
                          <span className="text-foreground-600 text-[10px] font-semibold">{plan.storageGB} GB</span>
                        </div>
                        {isCurrent ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full ${plan.badgeBg} ${plan.badgeText} text-[9px] font-bold`}>
                            <i className="ri-check-line text-[8px] mr-0.5"></i>Plan actual
                          </span>
                        ) : isSelected ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full ${plan.id === 'pro' ? 'bg-primary-500' : plan.id === 'premium' ? 'bg-accent-500' : 'bg-secondary-500'} text-background-50 text-[9px] font-bold`}>
                            Seleccionado
                          </span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full ${plan.badgeBg} ${plan.badgeText} text-[9px] font-bold`}>
                            {plan.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected plan summary */}
              {selectedPlanId && selectedPlanDef && (
                <div className={`rounded-xl p-4 border-2 ${selectedPlanDef.border} ${selectedPlanDef.bg}`}>
                  <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-3">Resumen del cambio</p>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-foreground-400">Plan actual</p>
                      <p className="text-foreground-800 font-bold">{currentPlanDef?.name} ({currentPlanDef?.price})</p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Nuevo plan</p>
                      <p className="text-foreground-800 font-bold" style={{ color: isUpgrade ? '#d97706' : isDowngrade ? '#dc2626' : undefined }}>
                        {selectedPlanDef.name} ({selectedPlanDef.price})
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Storage</p>
                      <p className={`font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : 'text-foreground-800'}`}>
                        {radio.storageTotalGB} GB → {selectedPlanDef.storageGB} GB ({storageDiff})
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Próx. renovación</p>
                      <p className="text-foreground-800 font-bold">{nextRenewal}</p>
                    </div>
                  </div>

                  {isUpgrade && gainedFeatures.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <p className="text-emerald-700 text-[10px] font-semibold mb-1.5">
                        <i className="ri-add-circle-line mr-1"></i>Nuevas funciones:
                      </p>
                      <div className="space-y-0.5">
                        {gainedFeatures.map((f) => (
                          <div key={f} className="flex items-center gap-2">
                            <i className="ri-check-line text-emerald-500 text-[10px]"></i>
                            <span className="text-emerald-700 text-[10px]">{f}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isDowngrade && !downgradeBlocked && (
                    <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="flex items-start gap-2">
                        <i className="ri-alert-line text-amber-600 text-sm mt-0.5 shrink-0"></i>
                        <div>
                          <p className="text-amber-800 text-[10px] font-semibold">Atención: downgrade</p>
                          <p className="text-amber-600 text-[9px] mt-0.5">
                            Se aplica en la próxima renovación. La radio podría perder funciones y almacenamiento.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedPlanDef.priceNum > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeAnnual}
                          onChange={(e) => setIncludeAnnual(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-background-300 peer-checked:bg-accent-500 rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                        <div>
                          <p className="text-foreground-800 text-xs font-semibold">Licencia anual (10 meses)</p>
                          <p className="text-foreground-400 text-[10px]">Ahorrá ~17% — ${annualPrice.toFixed(2)} en vez de ${(selectedPlanDef.priceNum * 12).toFixed(2)}</p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === 'confirm' && selectedPlanDef && (
            <>
              <button onClick={handleBack} className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line text-xs"></i>Volver a elegir plan
              </button>

              <div className="bg-background-100 rounded-xl border border-background-200 p-5">
                <h4 className="text-foreground-950 font-bold text-sm mb-4">
                  <i className="ri-file-list-3-line text-amber-600 mr-2"></i>
                  Resumen del cambio de plan
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-background-50 rounded-xl p-4 border border-background-200">
                      <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-1">Plan actual</p>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${currentPlanDef?.iconBg} flex items-center justify-center`}>
                          <i className={`${currentPlanDef?.icon} ${currentPlanDef?.iconColor} text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-bold">{currentPlanDef?.name}</p>
                          <p className="text-foreground-400 text-[10px]">{currentPlanDef?.price} · {radio.storageTotalGB} GB</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <p className="text-amber-600 text-[10px] font-semibold uppercase tracking-wide mb-1">
                        {isUpgrade ? 'Nuevo plan' : 'Nuevo plan (downgrade)'}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${selectedPlanDef.iconBg} flex items-center justify-center`}>
                          <i className={`${selectedPlanDef.icon} ${selectedPlanDef.iconColor} text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-bold">{selectedPlanDef.name}</p>
                          <p className="text-foreground-400 text-[10px]">{selectedPlanDef.price} · {selectedPlanDef.storageGB} GB</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-xl p-4 border border-background-200 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">{includeAnnual ? 'Licencia anual' : 'Cargo inmediato'}</span>
                      <span className="text-foreground-800 font-bold">
                        {monthlyPrice === 0 ? 'Gratis' : includeAnnual ? `$${annualPrice.toFixed(2)}` : `$${monthlyPrice.toFixed(2)}`}
                      </span>
                    </div>
                    {monthlyPrice > 0 && !includeAnnual && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-400">Licencia anual (opcional)</span>
                        <span className="text-foreground-500">${annualPrice.toFixed(2)}/año</span>
                      </div>
                    )}
                    <div className="border-t border-background-200 pt-2 flex items-center justify-between">
                      <span className="text-foreground-800 text-sm font-bold">Total hoy</span>
                      <span className="text-foreground-950 text-lg font-bold">
                        {totalToday === 0 ? 'Gratis' : `$${totalToday.toFixed(2)}`}
                      </span>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-xl p-4 border border-background-200 space-y-2">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Qué cambia</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">Storage</span>
                      <span className={`font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : 'text-foreground-600'}`}>{storageDiff}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">Próx. renovación</span>
                      <span className="text-foreground-600 font-medium">{nextRenewal}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-background-200 flex items-center gap-2 shrink-0 bg-background-50">
          {step === 'select' ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleContinue}
                disabled={!selectedPlanId}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-colors whitespace-nowrap ${
                  selectedPlanId
                    ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                    : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                }`}
              >
                {selectedPlanId ? (isUpgrade ? 'Continuar upgrade' : 'Revisar downgrade') : 'Seleccioná un plan'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs mr-1.5"></i>Volver
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmUpgrade}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold bg-foreground-950 text-background-50 hover:bg-foreground-800 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line text-sm mr-1.5"></i>
                Confirmar y generar invoice
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}